import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Card, Descriptions, Tag, Button, Space, Spin, message, Avatar, Tabs, Table, Empty
} from 'antd';
import {
  EditOutlined, ShopOutlined, MailOutlined, ToolOutlined, ShoppingOutlined, EuroOutlined
} from '@ant-design/icons';
import { useStores } from '../../stores';
import EditPartnerModal from '../../components/EditPartnerModal';
import BackButton from '../../components/BackButton';
import api from '../../services/api';

const PartnerDetail = observer(() => {
  const { t, i18n } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const { adminPartnerStore } = useStores();
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [services, setServices] = useState([]);
  const [products, setProducts] = useState([]);
  const [loadingServices, setLoadingServices] = useState(false);
  const [loadingProducts, setLoadingProducts] = useState(false);

  useEffect(() => {
    loadPartner();
    loadServices();
    loadProducts();
  }, [id]);

  const loadPartner = async () => {
    try {
      await adminPartnerStore.fetchPartnerById(id);
    } catch (error) {
      message.error(t('admin.partnerDetail.loadFailed'));
    }
  };

  const loadServices = async () => {
    setLoadingServices(true);
    try {
      const response = await api.get('/services/', { params: { partner: id } });
      setServices(response.data.results || response.data || []);
    } catch (error) {
      console.error('Failed to load services:', error);
    } finally {
      setLoadingServices(false);
    }
  };

  const loadProducts = async () => {
    setLoadingProducts(true);
    try {
      const response = await api.get('/products/', { params: { partner: id } });
      setProducts(response.data.results || response.data || []);
    } catch (error) {
      console.error('Failed to load products:', error);
    } finally {
      setLoadingProducts(false);
    }
  };

  const handleUpdate = async (partnerId, updateData, isFormData = false) => {
    await adminPartnerStore.updatePartner(partnerId, updateData, isFormData);
    await loadPartner(); // Reload to show updates
  };

  if (!adminPartnerStore.currentPartner) {
    return (
      <div style={{ padding: '24px', textAlign: 'center' }}>
        <Spin size="large" />
      </div>
    );
  }

  const partner = adminPartnerStore.currentPartner;

  return (
    <div style={{ padding: '24px' }}>
      <BackButton to="/admin/partners" label={t('admin.partnerDetail.backToPartners')} />

      {/* Partner Info Card */}
      <Card
        title={
          <Space>
            {partner.logo?.thumbnail ? (
              <Avatar
                size={48}
                src={partner.logo.thumbnail}
                style={{ border: '2px solid #f0f0f0' }}
              />
            ) : (
              <Avatar
                size={48}
                icon={<ShopOutlined />}
                style={{ backgroundColor: '#ff7a45' }}
              />
            )}
            <span style={{ fontSize: '20px', fontWeight: 'bold' }}>
              {partner.store_name}
            </span>
            <Tag color={partner.is_verified ? 'green' : 'orange'}>
              {partner.is_verified ? t('admin.partnerDetail.verified') : t('admin.partnerDetail.pendingVerification')}
            </Tag>
          </Space>
        }
        extra={
          <Button
            type="primary"
            icon={<EditOutlined />}
            onClick={() => setEditModalVisible(true)}
          >
            {t('admin.partnerDetail.editProfile')}
          </Button>
        }
      >
        <Descriptions bordered column={2}>
          <Descriptions.Item label={t('admin.partnerDetail.partnerId')}>{partner.id}</Descriptions.Item>
          <Descriptions.Item label={t('admin.partnerDetail.taxNumber')}>{partner.tax_number}</Descriptions.Item>

          <Descriptions.Item label={t('admin.partnerDetail.logo')}>
            {partner.logo?.medium ? (
              <img
                src={partner.logo.medium}
                alt={t('admin.partnerDetail.logoAlt')}
                style={{ maxWidth: 120, maxHeight: 120, borderRadius: 8 }}
              />
            ) : (
              <span style={{ color: '#999' }}>{t('admin.partnerDetail.noLogo')}</span>
            )}
          </Descriptions.Item>
          <Descriptions.Item label={t('admin.partnerDetail.platformFee')}>
            <Tag color="blue">{partner.platform_fee_percentage}%</Tag>
          </Descriptions.Item>

          <Descriptions.Item label={t('admin.partnerDetail.joinedDate')}>
            {new Date(partner.created_at).toLocaleDateString()}
          </Descriptions.Item>
          <Descriptions.Item label={<span><MailOutlined /> {t('admin.partnerDetail.email')}</span>}>
            {partner.user?.email || '-'}
          </Descriptions.Item>

          <Descriptions.Item label={t('admin.partnerDetail.address')} span={2}>
            {partner.address}<br />
            {partner.city}{partner.postal_code ? `, ${partner.postal_code}` : ''}<br />
            {partner.country}
          </Descriptions.Item>
        </Descriptions>
      </Card>

      {/* Statistics Cards */}
      <div style={{ marginTop: 24 }}>
        <Space size="large" style={{ width: '100%' }}>
          <Card style={{ flex: 1 }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#1890ff' }}>{products.length}</div>
              <div style={{ marginTop: 8, color: '#666' }}>{t('admin.partnerDetail.totalProducts')}</div>
            </div>
          </Card>
          <Card style={{ flex: 1 }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#52c41a' }}>{services.length}</div>
              <div style={{ marginTop: 8, color: '#666' }}>{t('admin.partnerDetail.totalServices')}</div>
            </div>
          </Card>
          <Card style={{ flex: 1 }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#faad14' }}>-</div>
              <div style={{ marginTop: 8, color: '#666' }}>{t('admin.partnerDetail.totalOrders')}</div>
            </div>
          </Card>
          <Card style={{ flex: 1 }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#13c2c2' }}>-</div>
              <div style={{ marginTop: 8, color: '#666' }}>{t('admin.partnerDetail.totalRevenue')}</div>
            </div>
          </Card>
        </Space>
      </div>

      {/* Services & Products Tabs */}
      <Card style={{ marginTop: 24 }}>
        <Tabs
          defaultActiveKey="services"
          items={[
            {
              key: 'services',
              label: (
                <span>
                  <ToolOutlined />
                  {t('admin.partnerDetail.services')} ({services.length})
                </span>
              ),
              children: (
                <Table
                  dataSource={services}
                  rowKey="id"
                  loading={loadingServices}
                  locale={{ emptyText: <Empty description={t('admin.partnerDetail.noServices')} /> }}
                  columns={[
                    {
                      title: t('admin.partnerDetail.serviceName'),
                      key: 'name',
                      render: (_, record) => i18n.language === 'el' ? (record.name_el || record.name_en) : record.name_en,
                    },
                    {
                      title: t('admin.partnerDetail.category'),
                      key: 'category',
                      render: (_, record) => i18n.language === 'el' ? (record.category_name_el || record.category_name_en) : record.category_name_en,
                    },
                    {
                      title: t('admin.partnerDetail.duration'),
                      dataIndex: 'duration',
                      key: 'duration',
                      render: (duration) => `${duration} min`,
                    },
                    {
                      title: t('admin.partnerDetail.price'),
                      dataIndex: 'price',
                      key: 'price',
                      render: (price) => <span><EuroOutlined /> {price}</span>,
                    },
                    {
                      title: t('admin.partnerDetail.status'),
                      key: 'status',
                      render: (_, record) => (
                        <Tag color={record.is_active ? 'green' : 'red'}>
                          {record.is_active ? t('common.active') : t('common.inactive')}
                        </Tag>
                      ),
                    },
                  ]}
                  pagination={{ pageSize: 5 }}
                />
              ),
            },
            {
              key: 'products',
              label: (
                <span>
                  <ShoppingOutlined />
                  {t('admin.partnerDetail.products')} ({products.length})
                </span>
              ),
              children: (
                <Table
                  dataSource={products}
                  rowKey="id"
                  loading={loadingProducts}
                  locale={{ emptyText: <Empty description={t('admin.partnerDetail.noProducts')} /> }}
                  columns={[
                    {
                      title: t('admin.partnerDetail.productName'),
                      key: 'title',
                      render: (_, record) => record.title || record.name || '-',
                    },
                    {
                      title: t('admin.partnerDetail.category'),
                      key: 'category',
                      render: (_, record) => {
                        if (!record.category) return '-';
                        return i18n.language === 'el' ? (record.category.name_el || record.category.name_en) : record.category.name_en;
                      },
                    },
                    {
                      title: t('admin.partnerDetail.price'),
                      key: 'price',
                      render: (_, record) => {
                        if (record.min_price && record.max_price && record.min_price !== record.max_price) {
                          return <span><EuroOutlined /> {record.min_price} - {record.max_price}</span>;
                        }
                        return <span><EuroOutlined /> {record.min_price || record.price || '-'}</span>;
                      },
                    },
                    {
                      title: t('admin.partnerDetail.rating'),
                      key: 'rating',
                      render: (_, record) => record.average_rating ? `${record.average_rating} ⭐` : '-',
                    },
                  ]}
                  pagination={{ pageSize: 5 }}
                />
              ),
            },
          ]}
        />
      </Card>

      {/* Edit Partner Modal */}
      <EditPartnerModal
        visible={editModalVisible}
        partner={partner}
        onClose={() => setEditModalVisible(false)}
        onUpdate={handleUpdate}
      />
    </div>
  );
});

export default PartnerDetail;
